import { Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SettingsProvider } from '@shared/services/settingsprovider';
import { Role } from '@shared/types/configuration';
import { ROLE_CLAIM } from '@shared/types/role.consts';
import * as Msal from 'msal';
import { User } from 'msal/lib-commonjs/User';
import { Observable, EMPTY, of, switchMap, from, first, toArray, map } from 'rxjs';

@Injectable()
export class MsalService {
    private tenantConfig: any;

    private clientApplication?: Msal.UserAgentApplication;

    constructor(private settingsProvider: SettingsProvider) {
        settingsProvider.configLoaded.subscribe(() => {
            this.tenantConfig = this.settingsProvider.configuration;
            this.clientApplication = new Msal.UserAgentApplication(
                this.tenantConfig.clientId,
                this.tenantConfig.authoritySignIn,
                this.authCallback.bind(this),
                // use local storage over session storage
                // to allow the user to work with multiple tabs.
                {
                    validateAuthority: false,
                    cacheLocation: 'localStorage',
                    navigateToLoginRequestUrl: false,
                    redirectUri:
                        window.location.protocol + '//' + window.location.host,
                    loadFrameTimeout: 6000,
                    postLogoutRedirectUri: window.location.protocol + '//' + window.location.host
                }
            );
        });
    }

    private setAuthority(authority: string) {
        if (this.clientApplication && this.clientApplication.authority !== authority) {
            this.clientApplication.authority = authority;
        }
    }

    public login(): void {
        // the desginId parameter can be used to control the look
        // of the policy.
        console.log('login...');
        if (this.clientApplication?.loginInProgress()) {
            return;
        }

        this.setAuthority(this.tenantConfig.authoritySignIn);
        this.clientApplication?.loginRedirect(this.tenantConfig.b2cScopes);
    }

    // This gets a cached or refreshed token from MSAL.
    // If no token is available, the promise is
    // rejected.
    public acquireTokenSilent(): Promise<string> | undefined {
        console.log('acquireTokenSilent...');
        if (this.clientApplication?.loginInProgress()) {
            return new Promise<string>(() => {});
        }

        this.setAuthority(this.tenantConfig.authoritySignIn);
        return this.clientApplication?.acquireTokenSilent(this.tenantConfig.b2cScopes)
            .then(
                token => {
                    return new Promise<string>((resolve, reject) => {
                        resolve(token);
                    });
                },
                error => {
                    if (error.indexOf('AADB2C90077') >= 0) {
                        this.clientApplication?.logout();
                    }
                    return new Promise<string>((resolve, reject) => {
                        resolve('');
                    });
                }
            );
    }

    logout(): void {
        this.setAuthority(this.tenantConfig.authoritySignIn);
        this.clientApplication?.logout();
    }

    isOnline(): boolean {
        return this.clientApplication?.getUser() != null;
    }

    public getUser(): User | undefined {
        return this.clientApplication?.getUser();
    }

    public getUserRoles$(): Observable<string> {
        if (this.clientApplication?.getUser() === null) {
            return EMPTY;
        }

        const role: string = (this.clientApplication?.getUser().idToken as any)[ROLE_CLAIM];

        if (!role) {
            return EMPTY;
        }

        return of(role).pipe(
            switchMap((value) => from(value.split(",").map((v: string) => v.trim())))
        );
    }

    public hasAnyRole(...roles: Role[]): boolean {
        let result: boolean = false;

        const roleNames = roles.map(v => v.toString());
        const subscription = this.getUserRoles$().pipe(first(value => roleNames.includes(value))).subscribe({
            next: _ => result = true,
            error: _ => result = false
        });

        subscription.unsubscribe();
        return result;
    }

    public isAdmin(): Signal<boolean> {
        return toSignal(
            this.getUserRoles$().pipe(toArray(), map(roles => this.calculateIsAdmin(roles))),
            {initialValue: false}
        )
    }

    calculateIsAdmin(roles: string[]): boolean {
        if (!roles) {
            return false;
        }
        const adminRoles: Role[] = ['APIAdmin', 'APIMManager'];
        return adminRoles.map(r => r.toString()).some(r => roles.includes(r));
    }

    private authCallback(
        errorDesc: any,
        token: any,
        error: any,
        tokenType: any
    ): void {
        if (errorDesc || error) {
            if (errorDesc.indexOf('AADB2C90118') > -1) {
                const userAgentApplication = window.msal as Msal.UserAgentApplication;
                userAgentApplication.authority = this.tenantConfig.authorityForgotPassword;
                userAgentApplication.loginRedirect(
                    this.tenantConfig.b2cScopes,
                    undefined
                );
            } else {
                console.log(error + ':' + errorDesc);
            }
        }
    }
}
